import { Article, Category, MediaFile } from './types';
import { post, get, del } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_NAME = 'helpApi';

const authHeaders = async (): Promise<Record<string, string>> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: token } : {};
  } catch {
    return {};
  }
};

// Articles — Public
export const listArticles = async (category?: string): Promise<Article[]> => {
  const path = category ? `/articles?category=${category}` : '/articles';
  const restOp = get({ apiName: API_NAME, path });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Article[];
};

export const getArticle = async (slug: string): Promise<Article> => {
  const restOp = get({ apiName: API_NAME, path: `/articles/${slug}` });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Article;
};

export const searchArticles = async (query: string): Promise<Article[]> => {
  const restOp = get({ apiName: API_NAME, path: `/articles/search?q=${encodeURIComponent(query)}` });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Article[];
};

// Articles — Admin
export const createArticle = async (article: Partial<Article>): Promise<Article> => {
  const headers = await authHeaders();
  const restOp = post({ apiName: API_NAME, path: '/admin/articles', options: { headers, body: article as any } });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Article;
};

export const updateArticle = async (id: string, article: Partial<Article>): Promise<Article> => {
  const headers = await authHeaders();
  const restOp = post({ apiName: API_NAME, path: `/admin/articles/${id}`, options: { headers, body: article as any } });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Article;
};

export const deleteArticle = async (id: string): Promise<void> => {
  const headers = await authHeaders();
  await del({ apiName: API_NAME, path: `/admin/articles/${id}`, options: { headers } }).response;
};

// Categories — Public
export const listCategories = async (): Promise<Category[]> => {
  const restOp = get({ apiName: API_NAME, path: '/categories' });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Category[];
};

// Categories — Admin
export const createCategory = async (category: Partial<Category>): Promise<Category> => {
  const headers = await authHeaders();
  const restOp = post({ apiName: API_NAME, path: '/admin/categories', options: { headers, body: category as any } });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Category;
};

export const updateCategory = async (id: string, category: Partial<Category>): Promise<Category> => {
  const headers = await authHeaders();
  const restOp = post({ apiName: API_NAME, path: `/admin/categories/${id}`, options: { headers, body: category as any } });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as Category;
};

export const deleteCategory = async (id: string): Promise<void> => {
  const headers = await authHeaders();
  await del({ apiName: API_NAME, path: `/admin/categories/${id}`, options: { headers } }).response;
};

// Media — upload via presigned URL
export const uploadMedia = async (file: File, folder: string): Promise<MediaFile> => {
  const headers = await authHeaders();

  // Get presigned upload URL from backend
  const restOp = post({
    apiName: API_NAME,
    path: '/admin/media/upload',
    options: {
      headers,
      body: {
        fileName: file.name,
        contentType: file.type,
        folder,
      } as any,
    },
  });
  const { body } = await restOp.response;
  const { uploadUrl, key, publicUrl } = (await body.json()) as any;

  // Upload directly to S3 via presigned URL
  await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  return {
    key,
    url: publicUrl,
    name: file.name,
    type: file.type,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
};

// Media — list uploaded files
export const listMedia = async (): Promise<MediaFile[]> => {
  const headers = await authHeaders();
  const restOp = get({ apiName: API_NAME, path: '/admin/media', options: { headers } });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as MediaFile[];
};

export const deleteMedia = async (key: string): Promise<{ deleted: string; updatedArticles: string[] }> => {
  const headers = await authHeaders();
  const restOp = del({
    apiName: API_NAME,
    path: '/admin/media',
    options: { headers, queryParams: { key } },
  });
  const { body } = await restOp.response;
  return (await body.json()) as unknown as { deleted: string; updatedArticles: string[] };
};
